import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  createPricingRule,
  deletePricingRule,
  listPricingRules,
  listServices,
  updatePricingRule,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type {
  CreatePricingRuleInput,
  PricingRule,
  PricingRuleKind,
  PricingRuleModifierBasis,
  PricingRuleModifierKind,
  ServiceCatalogueItem,
} from '@/lib/types';
import { ArrowDown, ArrowUp, BadgePercent, ExternalLink, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const KIND_LABELS: Record<PricingRuleKind, string> = {
  date_range: 'Date range',
  day_of_week: 'Day of week',
  length_of_stay: 'Length of stay',
  multi_pet: 'Multi-pet',
  lead_time: 'Lead time',
  addon: 'Addon',
  // Sub-spec 04+05 added these kinds via the API; the editor surface for them
  // ships in the per-vertical settings pages (DaycareCapacity for the family
  // discount, GroomerDetailPage for the groomer override). The labels below
  // exist so admin-side audit displays and the type-level Record check stay
  // exhaustive; the dedicated editors don't drop down through this widget.
  daycare_family_discount: 'Daycare family discount',
  groomer_specific: 'Groomer-specific override',
  // Constraint, not a price change: boarding stays overlapping the window
  // must be at least min_nights long. Customers get a 422; staff with
  // pricing.rules.write can override (audit-logged).
  min_stay: 'Minimum stay',
};

const KIND_OPTIONS: PricingRuleKind[] = [
  'date_range',
  'day_of_week',
  'length_of_stay',
  'multi_pet',
  'lead_time',
  'addon',
  'min_stay',
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
] as const;

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

/** Major-currency string -> minor integer. */
function majorToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

function minorToMajor(minor: number): string {
  return (minor / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Scope JSON helpers
// ---------------------------------------------------------------------------

interface DateRangeScope {
  from: string;
  to: string;
}
interface DayOfWeekScope {
  days: number[];
}
interface LengthOfStayScope {
  min_nights?: number;
  max_nights?: number;
}
interface MultiPetScope {
  from_pet_index: number;
}
interface LeadTimeScope {
  max_hours_before: number;
}
interface MinStayScope {
  start_date: string;
  end_date: string;
  min_nights: number;
}

function summariseScope(rule: PricingRule): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rule.scopeJson);
  } catch {
    return '(malformed scope)';
  }
  switch (rule.kind) {
    case 'date_range': {
      const s = parsed as Partial<DateRangeScope>;
      return s.from && s.to ? `${s.from} → ${s.to}` : '(no range)';
    }
    case 'day_of_week': {
      const s = parsed as Partial<DayOfWeekScope>;
      if (!Array.isArray(s.days) || s.days.length === 0) return '(no days)';
      return s.days
        .map((d) => DAYS_OF_WEEK.find((x) => x.value === d)?.label.slice(0, 3) ?? `?${d}`)
        .join(', ');
    }
    case 'length_of_stay': {
      const s = parsed as LengthOfStayScope;
      const parts: string[] = [];
      if (s.min_nights !== undefined) parts.push(`≥ ${s.min_nights}n`);
      if (s.max_nights !== undefined) parts.push(`≤ ${s.max_nights}n`);
      return parts.length > 0 ? parts.join(', ') : '(no bounds)';
    }
    case 'multi_pet': {
      const s = parsed as Partial<MultiPetScope>;
      return s.from_pet_index ? `From pet #${s.from_pet_index}` : '(no index)';
    }
    case 'lead_time': {
      const s = parsed as Partial<LeadTimeScope>;
      return s.max_hours_before !== undefined ? `≤ ${s.max_hours_before}h before` : '(no limit)';
    }
    case 'addon':
      return 'Always applies';
    case 'min_stay': {
      const s = parsed as Partial<MinStayScope>;
      if (!s.start_date || !s.end_date || s.min_nights === undefined) return '(no window)';
      return `${s.start_date} → ${s.end_date} · min ${s.min_nights}n`;
    }
    case 'daycare_family_discount':
      return 'Daycare family discount';
    case 'groomer_specific': {
      // Per-groomer override; surface a short summary, but the editor lives on
      // the GroomerDetailPage so this widget only needs to render the row.
      const s = (parsed ?? {}) as { groomer_id?: string; service_id?: string };
      const parts: string[] = [];
      if (s.groomer_id) parts.push(`Groomer ${s.groomer_id.slice(-6)}`);
      if (s.service_id) parts.push(`Service ${s.service_id.slice(-6)}`);
      return parts.join(' · ') || '(no scope)';
    }
  }
}

function modifierDisplay(rule: PricingRule, currency: string): string {
  if (rule.modifierKind === 'pct') {
    const sign = rule.modifierValue >= 0 ? '+' : '';
    return `${sign}${rule.modifierValue}%`;
  }
  // fixed_minor
  const sym = currencySymbol(currency);
  const sign = rule.modifierValue >= 0 ? '+' : '−';
  const abs = Math.abs(rule.modifierValue);
  return `${sign}${sym}${minorToMajor(abs)}`;
}

// ---------------------------------------------------------------------------
// Editor form
// ---------------------------------------------------------------------------

interface EditorFormState {
  serviceId: string; // '' = no service (account-default)
  kind: PricingRuleKind;
  label: string;
  modifierKind: PricingRuleModifierKind;
  modifierBasis: PricingRuleModifierBasis;
  // Stored as text-typed in major currency for fixed_minor; as plain percent for pct.
  modifierValueText: string;
  // Per-kind scope:
  fromDate: string;
  toDate: string;
  daysSelected: number[];
  minNights: string;
  maxNights: string;
  fromPetIndex: string;
  maxHoursBefore: string;
  // min_stay
  minStayStart: string;
  minStayEnd: string;
  minStayNights: string;
}

function emptyForm(): EditorFormState {
  return {
    serviceId: '',
    kind: 'addon',
    label: '',
    modifierKind: 'pct',
    modifierBasis: 'subtotal',
    modifierValueText: '',
    fromDate: '',
    toDate: '',
    daysSelected: [],
    minNights: '',
    maxNights: '',
    fromPetIndex: '2',
    maxHoursBefore: '',
    minStayStart: '',
    minStayEnd: '',
    minStayNights: '2',
  };
}

function buildScopeJson(
  form: EditorFormState,
): { ok: true; json: string } | { ok: false; error: string } {
  switch (form.kind) {
    case 'date_range': {
      if (!form.fromDate || !form.toDate)
        return { ok: false, error: 'From and to dates are required' };
      return { ok: true, json: JSON.stringify({ from: form.fromDate, to: form.toDate }) };
    }
    case 'day_of_week': {
      if (form.daysSelected.length === 0) return { ok: false, error: 'Pick at least one day' };
      return {
        ok: true,
        json: JSON.stringify({ days: form.daysSelected.slice().sort((a, b) => a - b) }),
      };
    }
    case 'length_of_stay': {
      const out: LengthOfStayScope = {};
      if (form.minNights.trim() !== '') {
        const v = Number(form.minNights);
        if (!Number.isInteger(v) || v < 1) return { ok: false, error: 'min_nights must be ≥ 1' };
        out.min_nights = v;
      }
      if (form.maxNights.trim() !== '') {
        const v = Number(form.maxNights);
        if (!Number.isInteger(v) || v < 1) return { ok: false, error: 'max_nights must be ≥ 1' };
        out.max_nights = v;
      }
      if (out.min_nights === undefined && out.max_nights === undefined) {
        return { ok: false, error: 'At least one of min/max nights is required' };
      }
      return { ok: true, json: JSON.stringify(out) };
    }
    case 'multi_pet': {
      const v = Number(form.fromPetIndex);
      if (!Number.isInteger(v) || v < 2)
        return { ok: false, error: 'from_pet_index must be an integer ≥ 2' };
      return { ok: true, json: JSON.stringify({ from_pet_index: v }) };
    }
    case 'lead_time': {
      if (form.maxHoursBefore.trim() === '')
        return { ok: false, error: 'max_hours_before is required' };
      const v = Number(form.maxHoursBefore);
      if (!Number.isInteger(v) || v < 0)
        return { ok: false, error: 'max_hours_before must be a non-negative integer' };
      return { ok: true, json: JSON.stringify({ max_hours_before: v }) };
    }
    case 'addon':
      return { ok: true, json: '{}' };
    case 'min_stay': {
      if (!form.minStayStart || !form.minStayEnd)
        return { ok: false, error: 'Start and end dates are required' };
      if (form.minStayEnd < form.minStayStart)
        return { ok: false, error: 'End date must be on or after the start date' };
      const v = Number(form.minStayNights);
      if (!Number.isInteger(v) || v < 1)
        return { ok: false, error: 'Minimum nights must be an integer ≥ 1' };
      return {
        ok: true,
        json: JSON.stringify({
          start_date: form.minStayStart,
          end_date: form.minStayEnd,
          min_nights: v,
        }),
      };
    }
    case 'daycare_family_discount':
    case 'groomer_specific':
      // These kinds are authored via the per-vertical settings pages, not this
      // generic editor. If the drawer is somehow opened on one (e.g. tenant
      // changes the kind dropdown), refuse to save until the dedicated editor
      // is used.
      return { ok: false, error: 'Edit this rule on the dedicated settings page.' };
  }
}

function buildModifierValue(
  form: EditorFormState,
): { ok: true; value: number } | { ok: false; error: string } {
  const text = form.modifierValueText.trim();
  if (text === '') return { ok: false, error: 'Modifier value is required' };
  if (form.modifierKind === 'pct') {
    const v = Number(text);
    if (!Number.isFinite(v)) return { ok: false, error: 'Percentage must be a number' };
    if (v < -100 || v > 200) return { ok: false, error: 'Percentage must be between -100 and 200' };
    return { ok: true, value: Math.trunc(v) };
  }
  // fixed_minor — interpret input as major currency
  const minor = majorToMinor(text);
  if (minor === null) return { ok: false, error: 'Amount must be a number' };
  return { ok: true, value: minor };
}

interface EditorDrawerProps {
  services: ServiceCatalogueItem[];
  defaultServiceId: string; // '' = account-default
  defaultPosition: number;
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}

function EditorDrawer({
  services,
  defaultServiceId,
  defaultPosition,
  currency,
  onClose,
  onSaved,
}: EditorDrawerProps) {
  const [form, setForm] = useState<EditorFormState>(() => ({
    ...emptyForm(),
    serviceId: defaultServiceId,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<string[] | null>(null);

  function toggleDay(d: number) {
    setForm((prev) => {
      const has = prev.daysSelected.includes(d);
      return {
        ...prev,
        daysSelected: has ? prev.daysSelected.filter((x) => x !== d) : [...prev.daysSelected, d],
      };
    });
  }

  async function handleSave() {
    setBusy(true);
    setError(null);
    setServerIssues(null);

    if (form.label.trim().length === 0) {
      setError('Label is required');
      setBusy(false);
      return;
    }
    const scope = buildScopeJson(form);
    if (!scope.ok) {
      setError(scope.error);
      setBusy(false);
      return;
    }
    // min_stay is a constraint, not a price change — no modifier to collect.
    // The API stores the neutral 'fixed_minor' / 0 pair for the NOT NULL columns.
    const isConstraint = form.kind === 'min_stay';
    const modifier = isConstraint ? ({ ok: true, value: 0 } as const) : buildModifierValue(form);
    if (!modifier.ok) {
      setError(modifier.error);
      setBusy(false);
      return;
    }

    const input: CreatePricingRuleInput = {
      serviceId: form.serviceId === '' ? null : form.serviceId,
      kind: form.kind,
      scopeJson: scope.json,
      modifierKind: isConstraint ? 'fixed_minor' : form.modifierKind,
      modifierValue: modifier.value,
      modifierBasis: isConstraint ? 'subtotal' : form.modifierBasis,
      label: form.label.trim(),
      position: defaultPosition,
    };

    try {
      await createPricingRule(input);
      onSaved();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const issues = (err.body as { issues?: unknown } | null)?.issues;
        if (Array.isArray(issues) && issues.every((s) => typeof s === 'string')) {
          setServerIssues(issues as string[]);
        } else {
          setError(err.code);
        }
      } else {
        setError('Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label="New pricing rule">
      <div className="flex flex-col p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">New pricing rule</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Kind */}
          <div>
            <label
              htmlFor="rule-kind"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Kind
            </label>
            <select
              id="rule-kind"
              value={form.kind}
              onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value as PricingRuleKind }))}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </div>

          {/* Service scope */}
          <div>
            <label
              htmlFor="rule-service"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Applies to
            </label>
            <select
              id="rule-service"
              value={form.serviceId}
              onChange={(e) => setForm((p) => ({ ...p, serviceId: e.target.value }))}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="">All services (account default)</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Per-kind scope builder */}
          {form.kind === 'date_range' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="rule-from"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  From
                </label>
                <input
                  id="rule-from"
                  type="date"
                  value={form.fromDate}
                  onChange={(e) => setForm((p) => ({ ...p, fromDate: e.target.value }))}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div>
                <label
                  htmlFor="rule-to"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  To
                </label>
                <input
                  id="rule-to"
                  type="date"
                  value={form.toDate}
                  onChange={(e) => setForm((p) => ({ ...p, toDate: e.target.value }))}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
          )}

          {form.kind === 'day_of_week' && (
            <div>
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Days
              </p>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((d) => (
                  <label key={d.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.daysSelected.includes(d.value)}
                      onChange={() => toggleDay(d.value)}
                      className="rounded border-border"
                    />
                    {d.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {form.kind === 'length_of_stay' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="rule-min-nights"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Min nights (optional)
                </label>
                <input
                  id="rule-min-nights"
                  type="number"
                  min={1}
                  value={form.minNights}
                  onChange={(e) => setForm((p) => ({ ...p, minNights: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div>
                <label
                  htmlFor="rule-max-nights"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Max nights (optional)
                </label>
                <input
                  id="rule-max-nights"
                  type="number"
                  min={1}
                  value={form.maxNights}
                  onChange={(e) => setForm((p) => ({ ...p, maxNights: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
          )}

          {form.kind === 'multi_pet' && (
            <div>
              <label
                htmlFor="rule-from-pet-index"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                From pet # (≥ 2)
              </label>
              <input
                id="rule-from-pet-index"
                type="number"
                min={2}
                value={form.fromPetIndex}
                onChange={(e) => setForm((p) => ({ ...p, fromPetIndex: e.target.value }))}
                className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          )}

          {form.kind === 'lead_time' && (
            <div>
              <label
                htmlFor="rule-max-hours-before"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Max hours before check-in
              </label>
              <input
                id="rule-max-hours-before"
                type="number"
                min={0}
                value={form.maxHoursBefore}
                onChange={(e) => setForm((p) => ({ ...p, maxHoursBefore: e.target.value }))}
                className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          )}

          {form.kind === 'min_stay' && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Boarding stays with at least one night inside this window must be at least the
                minimum length. Customers can&rsquo;t book a shorter stay online; staff with pricing
                permissions can override (recorded in the audit log).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="rule-min-stay-start"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Window start
                  </label>
                  <input
                    id="rule-min-stay-start"
                    type="date"
                    value={form.minStayStart}
                    onChange={(e) => setForm((p) => ({ ...p, minStayStart: e.target.value }))}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="rule-min-stay-end"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Window end
                  </label>
                  <input
                    id="rule-min-stay-end"
                    type="date"
                    value={form.minStayEnd}
                    onChange={(e) => setForm((p) => ({ ...p, minStayEnd: e.target.value }))}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="rule-min-stay-nights"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Minimum nights
                </label>
                <input
                  id="rule-min-stay-nights"
                  type="number"
                  min={1}
                  step={1}
                  value={form.minStayNights}
                  onChange={(e) => setForm((p) => ({ ...p, minStayNights: e.target.value }))}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
          )}

          {/* Modifier — hidden for min_stay (a constraint, not a price change) */}
          {form.kind !== 'min_stay' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="rule-mod-kind"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Modifier type
                  </label>
                  <select
                    id="rule-mod-kind"
                    value={form.modifierKind}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        modifierKind: e.target.value as PricingRuleModifierKind,
                        modifierValueText: '',
                      }))
                    }
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="pct">Percentage</option>
                    <option value="fixed_minor">Fixed amount</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="rule-mod-value"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {form.modifierKind === 'pct'
                      ? 'Percentage'
                      : `Amount (${currencySymbol(currency)})`}
                  </label>
                  <input
                    id="rule-mod-value"
                    type="number"
                    step={form.modifierKind === 'pct' ? '1' : '0.01'}
                    value={form.modifierValueText}
                    onChange={(e) => setForm((p) => ({ ...p, modifierValueText: e.target.value }))}
                    placeholder={form.modifierKind === 'pct' ? '10' : '5.00'}
                    className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="rule-mod-basis"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Applies to
                </label>
                <select
                  id="rule-mod-basis"
                  value={form.modifierBasis}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      modifierBasis: e.target.value as PricingRuleModifierBasis,
                    }))
                  }
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="subtotal">Running subtotal</option>
                  <option value="base">Base price only</option>
                </select>
              </div>
            </>
          )}

          {/* Label */}
          <div>
            <label
              htmlFor="rule-label"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Label
            </label>
            <input
              id="rule-label"
              type="text"
              value={form.label}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              placeholder="Christmas surcharge"
              maxLength={80}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* Errors */}
          {error !== null && <p className="text-sm text-destructive">{error}</p>}
          {serverIssues !== null && (
            <ul className="space-y-1 text-sm text-destructive">
              {serverIssues.map((iss) => (
                <li key={iss}>• {iss}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save rule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Delete confirm dialog
// ---------------------------------------------------------------------------

interface DeleteConfirmProps {
  rule: PricingRule;
  onCancel: () => void;
  onConfirmed: () => void;
}

function DeleteConfirm({ rule, onCancel, onConfirmed }: DeleteConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await deletePricingRule(rule.id);
      onConfirmed();
    } catch (err) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md" aria-label="Confirm delete pricing rule">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Delete "{rule.label}"?</h2>
        <p className="text-sm text-muted-foreground">
          The rule will be deactivated and excluded from future price calculations. Past quotes are
          unaffected.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ModalState = { kind: 'none' } | { kind: 'add' } | { kind: 'delete'; rule: PricingRule };

export function PricingRulesSettings() {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const currency = account?.currency ?? 'GBP';
  const [searchParams] = useSearchParams();

  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [filterServiceId, setFilterServiceId] = useState<string>(searchParams.get('service') ?? ''); // '' = all
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const filterArg = filterServiceId === '' ? undefined : filterServiceId;
      const res = await listPricingRules(filterArg);
      setRules(res.rules);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, [filterServiceId]);

  useEffect(() => {
    void (async () => {
      try {
        const svcs = await listServices();
        setServices(svcs);
      } catch {
        // Ignore — page still works without services list (account-default rules only).
      }
    })();
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const nextPosition = useMemo(() => {
    return rules.reduce((max, r) => Math.max(max, r.position), -1) + 1;
  }, [rules]);

  function serviceName(id: string | null): string {
    if (id === null) return 'All services';
    return services.find((s) => s.id === id)?.name ?? id;
  }

  async function moveUp(idx: number) {
    if (idx <= 0) return;
    const a = rules[idx];
    const b = rules[idx - 1];
    if (!a || !b) return;
    try {
      await Promise.all([
        updatePricingRule(a.id, { position: b.position }),
        updatePricingRule(b.id, { position: a.position }),
      ]);
      void refresh();
    } catch {
      // surface no UI for this v1 — refresh shows current truth
      void refresh();
    }
  }

  async function moveDown(idx: number) {
    if (idx >= rules.length - 1) return;
    const a = rules[idx];
    const b = rules[idx + 1];
    if (!a || !b) return;
    try {
      await Promise.all([
        updatePricingRule(a.id, { position: b.position }),
        updatePricingRule(b.id, { position: a.position }),
      ]);
      void refresh();
    } catch {
      void refresh();
    }
  }

  async function renumber() {
    try {
      await Promise.all(
        rules.map((r, i) =>
          r.position === i
            ? Promise.resolve({ rule: r })
            : updatePricingRule(r.id, { position: i }),
        ),
      );
      void refresh();
    } catch {
      void refresh();
    }
  }

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage pricing rules
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Pricing rules</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Surcharges and discounts applied on top of the service base price. Rules are evaluated
            in order; later rules can compound on the running subtotal or apply against the base
            only.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'add' })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          New rule
        </button>
      </header>

      {/* Base price callout — helps tenants understand the two-layer pricing model */}
      {services.filter((s) => s.isActive === 1).length > 0 && (
        <section className="rounded-xl border border-border bg-background p-4">
          <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Service base prices
          </p>
          <p className="mb-3 text-xs text-muted-foreground">
            These are the starting rates before any pricing rules below are applied. Edit base
            prices in{' '}
            <a
              href="/settings/services"
              className="inline-flex items-center gap-0.5 text-primary underline"
            >
              Settings → Services
              <ExternalLink className="size-3" strokeWidth={1.75} />
            </a>
            .
          </p>
          <div className="divide-y divide-border">
            {services
              .filter(
                (s) => s.isActive === 1 && ['boarding', 'daycare', 'grooming'].includes(s.category),
              )
              .map((svc) => (
                <div key={svc.id} className="flex items-center justify-between py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{svc.name}</span>
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {svc.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="num font-medium">
                      {formatPriceMinor(svc.defaultPriceMinor, currency)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {svc.category === 'boarding'
                        ? '/ night'
                        : svc.category === 'daycare'
                          ? '/ visit'
                          : '/ appointment'}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Filter */}
      <div className="mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label
            htmlFor="filter-service"
            className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Filter by service
          </label>
          <select
            id="filter-service"
            value={filterServiceId}
            onChange={(e) => setFilterServiceId(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">All</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        {rules.length > 0 && (
          <button
            type="button"
            onClick={() => void renumber()}
            className="text-xs text-muted-foreground hover:text-foreground press"
          >
            Reset position numbering
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : rules.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <BadgePercent className="mx-auto mb-3 size-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium text-foreground">No pricing rules configured.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add a rule to apply a surcharge or discount to your services.
          </p>
          <button
            type="button"
            onClick={() => setModal({ kind: 'add' })}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            New rule
          </button>
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="grid grid-cols-[3rem_8rem_1fr_5rem_1fr_8rem_1fr_auto] gap-3 border-b border-border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            <span>#</span>
            <span>Kind</span>
            <span>Label</span>
            <span>Modifier</span>
            <span>Scope</span>
            <span>Service</span>
            <span>Status</span>
            <span />
          </div>
          <div className="divide-y divide-border">
            {rules.map((rule, idx) => (
              <div
                key={rule.id}
                className="grid grid-cols-[3rem_8rem_1fr_5rem_1fr_8rem_1fr_auto] items-center gap-3 px-6 py-3.5 text-sm"
              >
                <span className="num text-muted-foreground">{rule.position}</span>
                <span className="inline-flex w-fit items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-foreground ring-hairline">
                  {KIND_LABELS[rule.kind]}
                </span>
                <span className="font-medium">{rule.label}</span>
                <span className="num text-foreground">{modifierDisplay(rule, currency)}</span>
                <span className="text-muted-foreground">{summariseScope(rule)}</span>
                <span className="text-muted-foreground">{serviceName(rule.serviceId)}</span>
                <span>
                  {rule.active === 1 ? (
                    <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
                      Inactive
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => void moveUp(idx)}
                    disabled={idx === 0}
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground press disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="size-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void moveDown(idx)}
                    disabled={idx === rules.length - 1}
                    className="rounded-md p-1 text-muted-foreground hover:text-foreground press disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="size-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setModal({ kind: 'delete', rule })}
                    className="rounded-md p-1 text-muted-foreground hover:text-destructive press"
                    title="Delete rule"
                  >
                    <Trash2 className="size-3.5" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Modals */}
      {modal.kind === 'add' && (
        <EditorDrawer
          services={services}
          defaultServiceId={filterServiceId}
          defaultPosition={nextPosition}
          currency={currency}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'delete' && (
        <DeleteConfirm
          rule={modal.rule}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
    </div>
  );
}
